import { IGuest } from '@bridebook/models/source/models/Weddings/Guests.types';
import { IGuestGroup } from './create-message-flow/recipients/use-guests-groups';
import { keyBy } from 'lodash';

/**
 * Creates a map of guests where each key is the guest ID (like in Firebase)
 * Used for O(1) access to guest data by ID instead of array traversal
 * @param guests - Array of guest objects
 * @returns Object map of guest ID -> guest object
 */

export const getGuestsMap = (guests: IGuest[]): Record<string, IGuest> =>
  keyBy(guests, (guest) => guest.id);

export const hasEmail = (guest: IGuest) => guest?.contacts?.email;

type TGuestWithEmail = IGuest & { contacts: { email: string } };

// type guard helper to get a guest that always has an email
export const isGuestWithEmail = (guest: IGuest): guest is TGuestWithEmail =>
  guest?.contacts?.email !== undefined && guest?.contacts?.email !== '';

export const getGuestsWithEmail = (teams: IGuest[][]): TGuestWithEmail[] =>
  teams.flatMap((team) => team.filter(isGuestWithEmail));

export const filterSelectedGuests = (guests: IGuest[], selectedIds: Set<string>): IGuest[] =>
  guests.filter((guest) => selectedIds.has(guest.id));

export const groupGuestsByHead = (guests: IGuest[]): IGuest[][] => {
  // First, create a map of head IDs to their team members
  const teamMap = new Map<string, IGuest[]>();
  const processedGuests = new Set<string>();

  // Add all guests that have a head to their respective teams
  guests.forEach((guest) => {
    if (guest.head) {
      const team = teamMap.get(guest.head) || [];
      team.push(guest);
      teamMap.set(guest.head, team);
    }
  });

  // Now process all guests to create the final teams
  const teams: IGuest[][] = [];
  guests.forEach((guest) => {
    // Skip if already processed
    if (processedGuests.has(guest.id)) return;

    if (guest.head) {
      // If this guest has a head but the head doesn't exist in our guest list,
      // create a team with just the members pointing to this head
      const headExists = guests.some((g) => g.id === guest.head);
      if (!headExists) {
        const teamMembers = teamMap.get(guest.head) || [];
        teams.push([...teamMembers]);
        teamMembers.forEach((member) => processedGuests.add(member.id));
      }
      return;
    }

    // Get this guest's team members (if any)
    const teamMembers = teamMap.get(guest.id) || [];

    // Create a team with this guest as head and all their team members. Sort by name.
    const thisGuestAndHead = [guest, ...teamMembers.sort((a, b) => a.name.localeCompare(b.name))];
    teams.push(thisGuestAndHead);
    processedGuests.add(guest.id);
    teamMembers.forEach((member) => processedGuests.add(member.id));
  });

  // Sort teams by the name of the first guest in each team
  return teams.sort((a, b) => a[0].name.localeCompare(b[0].name));
};

export const getGuestsMapByHead = (guests: IGuest[]): Record<string, IGuest[]> => {
  const guestsMap = new Map<string, IGuest[]>();

  // First pass: Add all guests that have a head to their respective teams
  guests.forEach((guest) => {
    const head = guest.head;
    if (head) {
      guestsMap.set(head, [...(guestsMap.get(head) || []), guest]);
    }
  });

  // Second pass: Ensure all heads (guests without a head property) exist in the map
  guests.forEach((guest) => {
    if (!guest.head) {
      // If this guest is a head but not already in the map, add them with just themselves
      if (!guestsMap.has(guest.id)) {
        guestsMap.set(guest.id, [guest]);
      } else {
        // If they already have team members, add themselves to the front of the array
        const existingTeam = guestsMap.get(guest.id) || [];
        guestsMap.set(guest.id, [guest, ...existingTeam]);
      }
    }
  });

  return Object.fromEntries(guestsMap);
};

export const getSelectedGuestIdsFromGroup = (group: IGuestGroup, selectedGuestIds: Set<string>) =>
  group.teams.flatMap((team) =>
    team.filter((guest) => selectedGuestIds.has(guest.id)).map((guest) => guest.id),
  );

export const getRecipientsStrings = (guests: IGuest[]): string[] => {
  // theoretically this function should be used only for guests with emails. But we add this check to have type safety
  const guestsWithEmail = guests.filter(isGuestWithEmail);
  const recipients: string[] = [];
  guestsWithEmail.forEach((guest) => {
    if (guest.head) {
      // if the guest has a head, add the email and guest's name. This is because the email is the head's email (strange business logic - but it's what we have to work with)
      recipients.push(`${guest.contacts.email} (${guest.name})`);
    } else {
      // if the guest doesn't have a head, add the email only
      recipients.push(`${guest.contacts.email}`);
    }
  });
  return recipients;
};
